import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Paper,
  Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import {
  BankFileTemplateService, PaymentApprovalBandService, PaymentRunService,
} from '../../../services/paymentRun.service';
import { AccountService } from '../../../services/account.service';
import { AuthService } from '../../../services/auth.service';
import {
  ApprovalBand, BankFileFieldInfo, BankFileFormat, BankFileTemplate,
} from '../../../interfaces/procurement/paymentRun.interface';
import { Account } from '../../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../../common/AccountAutocomplete';
import { leafAccounts } from '../../../utils/postingAccounts';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import ScrollableTable from '../../common/ScrollableTable';
import { useToast } from '../../../context/ToastContext';
import { formatMoney } from '../../../utils/format';

const FORMATS: BankFileFormat[] = ['BEFTN', 'RTGS', 'NPSB'];

/** Procurement > Payment Settings: discount account, approval bands, bank-file templates (W3-P). */
export const PaymentSettings: React.FC = () => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution()!!;

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [discountAccountId, setDiscountAccountId] = useState('');
  const [bands, setBands] = useState<ApprovalBand[]>([]);
  const [templates, setTemplates] = useState<BankFileTemplate[]>([]);
  const [fields, setFields] = useState<BankFileFieldInfo[]>([]);
  const [bandEdit, setBandEdit] = useState<ApprovalBand | null>(null);
  const [tplEdit, setTplEdit] = useState<BankFileTemplate | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{ kind: 'band' | 'template'; id: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [acc, settings, b, t, f] = await Promise.all([
        AccountService.getAllAccounts(),
        PaymentRunService.getSettings(institutionId).catch(() => null),
        PaymentApprovalBandService.list(institutionId),
        BankFileTemplateService.list(institutionId),
        BankFileTemplateService.fields(),
      ]);
      setAccounts(acc ?? []);
      setDiscountAccountId(settings?.discountReceivedAccountId ?? '');
      setBands(b ?? []);
      setTemplates(t ?? []);
      setFields(f ?? []);
    } catch (e) {
      toast.showApiError(e, 'Failed to load payment settings');
    }
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const guard = async (fn: () => Promise<unknown>, ok: string, fail: string) => {
    setSaving(true);
    try {
      await fn();
      toast.showSuccess(ok);
      await load();
    } catch (e) {
      toast.showApiError(e, fail);
    } finally {
      setSaving(false);
    }
  };

  const saveBand = async () => {
    if (!bandEdit) return;
    const b = bandEdit;
    await guard(() => (b.id ? PaymentApprovalBandService.update(b.id, b) : PaymentApprovalBandService.create(b)),
      'Approval band saved', 'Failed to save the band');
    setBandEdit(null);
  };

  const saveTemplate = async () => {
    if (!tplEdit) return;
    const t = tplEdit;
    await guard(() => (t.id ? BankFileTemplateService.update(t.id, t) : BankFileTemplateService.create(t)),
      'Template saved', 'Failed to save the template');
    setTplEdit(null);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const { kind, id } = pendingDelete;
    await guard(() => (kind === 'band' ? PaymentApprovalBandService.remove(id) : BankFileTemplateService.remove(id)),
      'Removed', 'Failed to remove');
    setPendingDelete(null);
  };

  const toggleColumn = (key: string) => setTplEdit(prev => {
    if (!prev) return prev;
    const has = prev.columns.includes(key);
    return { ...prev, columns: has ? prev.columns.filter(c => c !== key) : [...prev.columns, key] };
  });

  return (
    <Box data-help-page="paymentRun.settings.page" sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Payment Settings</Typography>
      <Stack spacing={3}>
        <Paper data-help-section="paymentRun.settings.discount" sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Discount received account</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Early-payment discounts taken in a payment run are credited here. Required before a run can take a discount.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <Box sx={{ flex: '1 1 320px', maxWidth: 480 }}>
              <AccountAutocomplete data-help="paymentRun.settings.discountAccount" accounts={leafAccounts(accounts)} value={discountAccountId} label="Discount received account"
                placeholder="None" onChange={setDiscountAccountId} />
            </Box>
            <Button data-help="paymentRun.settings.saveDiscount" variant="contained" disabled={saving}
              onClick={() => guard(() => PaymentRunService.saveSettings(institutionId, discountAccountId || null), 'Saved', 'Failed to save')}>
              Save
            </Button>
          </Box>
        </Paper>

        <Paper data-help-section="paymentRun.settings.bands" sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6">Approval bands</Typography>
            <Button data-help="paymentRun.settings.addBand" variant="contained" onClick={() => setBandEdit({
              institutionId, sequenceNo: bands.length + 1, minAmount: 0, maxAmount: null, approvalsRequired: 1,
            })}>Add band</Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            The run total picks how many different approvers must approve it. With no band, one approval is enough.
            The person who made the run can never approve it.
          </Typography>
          {bands.length === 0 ? <Alert severity="info">No bands: every run needs one approval.</Alert> : (
            <ScrollableTable>
              <Table data-help-section="paymentRun.settings.bandTable" size="small">
                <TableHead>
                  <TableRow><TableCell data-help="paymentRun.settings.band.col.order">#</TableCell><TableCell data-help="paymentRun.settings.band.col.from" align="right">From</TableCell><TableCell data-help="paymentRun.settings.band.col.to" align="right">To</TableCell>
                    <TableCell data-help="paymentRun.settings.band.col.approvals" align="right">Approvals</TableCell><TableCell data-help="paymentRun.settings.band.col.actions" align="right">Actions</TableCell></TableRow>
                </TableHead>
                <TableBody>
                  {bands.map(b => (
                    <TableRow key={b.id}>
                      <TableCell>{b.sequenceNo}</TableCell>
                      <TableCell align="right">{formatMoney(b.minAmount)}</TableCell>
                      <TableCell align="right">{b.maxAmount == null ? 'No limit' : formatMoney(b.maxAmount)}</TableCell>
                      <TableCell align="right">{b.approvalsRequired}</TableCell>
                      <TableCell align="right">
                        <Button data-help="paymentRun.settings.band.action.edit" size="small" onClick={() => setBandEdit({ ...b })}>Edit</Button>
                        <Button data-help="paymentRun.settings.band.action.remove" size="small" color="error" onClick={() => setPendingDelete({ kind: 'band', id: b.id! })}>Remove</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollableTable>
          )}
        </Paper>

        <Paper data-help-section="paymentRun.settings.templates" sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6">Bank-file templates</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button data-help="paymentRun.settings.loadDefaults" variant="outlined" disabled={saving}
                onClick={() => guard(async () => {
                  const r = await BankFileTemplateService.loadDefaults(institutionId);
                  toast.showSuccess(`${r.created} template(s) added, ${r.skipped} already present`);
                }, 'Defaults loaded', 'Failed to load the defaults')}>
                Load defaults
              </Button>
              <Button data-help="paymentRun.settings.addTemplate" variant="contained" onClick={() => setTplEdit({
                institutionId, name: '', format: 'BEFTN', delimiter: ',', hasHeader: true, columns: [], dateFormat: 'dd-MM-yyyy',
              })}>Add template</Button>
            </Box>
          </Box>
          <Alert severity="warning" sx={{ mb: 1 }}>
            The defaults are generic layouts. Confirm the exact column order and the minimum and maximum amounts with your bank before uploading a file.
          </Alert>
          {templates.length === 0 ? <Typography color="text.secondary">No templates yet.</Typography> : (
            <ScrollableTable>
              <Table data-help-section="paymentRun.settings.templateTable" size="small">
                <TableHead>
                  <TableRow><TableCell data-help="paymentRun.settings.template.col.name">Name</TableCell><TableCell data-help="paymentRun.settings.template.col.format">Format</TableCell><TableCell data-help="paymentRun.settings.template.col.columns">Columns</TableCell>
                    <TableCell data-help="paymentRun.settings.template.col.min" align="right">Min</TableCell><TableCell data-help="paymentRun.settings.template.col.max" align="right">Max</TableCell>
                    <TableCell data-help="paymentRun.settings.template.col.actions" align="right">Actions</TableCell></TableRow>
                </TableHead>
                <TableBody>
                  {templates.map(t => (
                    <TableRow key={t.id}>
                      <TableCell>{t.name}</TableCell><TableCell>{t.format}</TableCell>
                      <TableCell>{t.columns.length}</TableCell>
                      <TableCell align="right">{t.minAmount == null ? '—' : formatMoney(t.minAmount)}</TableCell>
                      <TableCell align="right">{t.maxAmount == null ? '—' : formatMoney(t.maxAmount)}</TableCell>
                      <TableCell align="right">
                        <Button data-help="paymentRun.settings.template.action.edit" size="small" onClick={() => setTplEdit({ ...t, columns: [...t.columns] })}>Edit</Button>
                        <Button data-help="paymentRun.settings.template.action.remove" size="small" color="error" onClick={() => setPendingDelete({ kind: 'template', id: t.id! })}>Remove</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollableTable>
          )}
        </Paper>
      </Stack>

      <Dialog data-help-page="procurement.payBandForm.page" open={!!bandEdit} onClose={() => setBandEdit(null)} fullWidth maxWidth="xs">
        <DialogTitle>{bandEdit?.id ? 'Edit band' : 'Add band'}</DialogTitle>
        <DialogContent>
          {bandEdit && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField data-help="procurement.payBandForm.field.order" label="Order" type="number" size="small" fullWidth value={bandEdit.sequenceNo}
                onChange={e => setBandEdit({ ...bandEdit, sequenceNo: Number(e.target.value) })} />
              <TextField data-help="procurement.payBandForm.field.from" label="Total from" type="number" size="small" fullWidth value={bandEdit.minAmount}
                onChange={e => setBandEdit({ ...bandEdit, minAmount: Number(e.target.value) })} />
              <TextField data-help="procurement.payBandForm.field.to" label="Total up to (blank = no limit)" type="number" size="small" fullWidth value={bandEdit.maxAmount ?? ''}
                onChange={e => setBandEdit({ ...bandEdit, maxAmount: e.target.value === '' ? null : Number(e.target.value) })} />
              <TextField data-help="procurement.payBandForm.field.approvals" select label="Approvals required" size="small" fullWidth value={bandEdit.approvalsRequired}
                onChange={e => setBandEdit({ ...bandEdit, approvalsRequired: Number(e.target.value) })}>
                <MenuItem value={1}>1</MenuItem><MenuItem value={2}>2</MenuItem>
              </TextField>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button data-help="procurement.payBandForm.cancel" onClick={() => setBandEdit(null)}>Cancel</Button>
          <Button data-help="procurement.payBandForm.save" variant="contained" onClick={saveBand} disabled={saving}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog data-help-page="procurement.bankTplForm.page" open={!!tplEdit} onClose={() => setTplEdit(null)} fullWidth maxWidth="md">
        <DialogTitle>{tplEdit?.id ? 'Edit template' : 'Add template'}</DialogTitle>
        <DialogContent>
          {tplEdit && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <TextField data-help="procurement.bankTplForm.field.name" label="Name" size="small" required value={tplEdit.name} sx={{ flex: '1 1 220px' }}
                  onChange={e => setTplEdit({ ...tplEdit, name: e.target.value })} />
                <TextField data-help="procurement.bankTplForm.field.bank" label="Bank" size="small" value={tplEdit.bankName ?? ''} sx={{ flex: '1 1 180px' }}
                  onChange={e => setTplEdit({ ...tplEdit, bankName: e.target.value })} />
                <TextField data-help="procurement.bankTplForm.field.format" select label="Format" size="small" value={tplEdit.format} sx={{ flex: '1 1 140px' }}
                  onChange={e => setTplEdit({ ...tplEdit, format: e.target.value as BankFileFormat })}>
                  {FORMATS.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                </TextField>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                <TextField data-help="procurement.bankTplForm.field.delimiter" select label="Delimiter" size="small" value={tplEdit.delimiter} sx={{ flex: '1 1 120px', maxWidth: 160 }}
                  onChange={e => setTplEdit({ ...tplEdit, delimiter: e.target.value })}>
                  {[[',', 'Comma'], [';', 'Semicolon'], ['|', 'Pipe'], ['\t', 'Tab']].map(([v, l]) => <MenuItem key={l} value={v}>{l}</MenuItem>)}
                </TextField>
                <TextField data-help="procurement.bankTplForm.field.dateFormat" label="Date format" size="small" value={tplEdit.dateFormat} sx={{ flex: '1 1 140px', maxWidth: 200 }}
                  onChange={e => setTplEdit({ ...tplEdit, dateFormat: e.target.value })} />
                <TextField data-help="procurement.bankTplForm.field.min" label="Minimum amount" type="number" size="small" value={tplEdit.minAmount ?? ''} sx={{ flex: '1 1 140px', maxWidth: 200 }}
                  onChange={e => setTplEdit({ ...tplEdit, minAmount: e.target.value === '' ? null : Number(e.target.value) })} />
                <TextField data-help="procurement.bankTplForm.field.max" label="Maximum amount" type="number" size="small" value={tplEdit.maxAmount ?? ''} sx={{ flex: '1 1 140px', maxWidth: 200 }}
                  onChange={e => setTplEdit({ ...tplEdit, maxAmount: e.target.value === '' ? null : Number(e.target.value) })} />
                <FormControlLabel label="Header row" control={<Checkbox data-help="procurement.bankTplForm.field.header" checked={tplEdit.hasHeader}
                  onChange={e => setTplEdit({ ...tplEdit, hasHeader: e.target.checked })} />} />
              </Box>
              <Typography variant="subtitle2">Columns, in the order picked</Typography>
              <Typography variant="body2" color="text.secondary">
                {tplEdit.columns.length === 0 ? 'None picked.' : tplEdit.columns
                  .map(k => fields.find(f => f.key === k)?.label ?? k).join('  >  ')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {fields.map(f => (
                  <FormControlLabel key={f.key} label={f.label} sx={{ flex: '1 1 220px', maxWidth: 260 }}
                    control={<Checkbox data-help="procurement.bankTplForm.field.columns" size="small" checked={tplEdit.columns.includes(f.key)} onChange={() => toggleColumn(f.key)} />} />
                ))}
              </Box>
              <TextField data-help="procurement.bankTplForm.field.notes" label="Notes" size="small" fullWidth value={tplEdit.notes ?? ''}
                onChange={e => setTplEdit({ ...tplEdit, notes: e.target.value })} />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button data-help="procurement.bankTplForm.cancel" onClick={() => setTplEdit(null)}>Cancel</Button>
          <Button data-help="procurement.bankTplForm.save" variant="contained" onClick={saveTemplate}
            disabled={saving || !tplEdit?.name.trim() || !tplEdit?.columns.length || !tplEdit?.delimiter}>Save</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog open={!!pendingDelete} title="Remove" message="Remove this item? Runs already made are not affected."
        confirmLabel="Remove" confirmColor="error" submitting={saving}
        onCancel={() => setPendingDelete(null)} onConfirm={confirmDelete} />
    </Box>
  );
};

export default PaymentSettings;
